import { useState, useEffect, useMemo } from 'react';
import { API, formatWeight, naturalBoxSort } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Eye, Pencil, Trash2, Lock, AlertTriangle, RotateCcw, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const sanitizeDecimal = (v) => { let c = v.replace(/[^0-9.]/g, ''); const p = c.split('.'); if (p.length > 2) c = p[0] + '.' + p.slice(1).join(''); return c; };

export default function BoxesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [search, setSearch] = useState('');
  const [purityFilter, setPurityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('approved');
  const [showCreate, setShowCreate] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [form, setForm] = useState({ box_id: '', box_type: 'regular', gold_purity: '76', description: '', gross_weight_g: '', polyethene_weight_g: '', box_weight_g: '', selectedItems: [] });
  const [editForm, setEditForm] = useState({});
  const { busy: loading, run } = useSubmitGuard('Please wait — the previous action is still processing');
  const [quickAddName, setQuickAddName] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [editItemSearch, setEditItemSearch] = useState('');
  const [sortCol, setSortCol] = useState('box_id');
  const [sortDir, setSortDir] = useState('asc');
  const canEdit = ['admin', 'store_manager', 'analyst'].includes(user?.role);
  const canManageItems = ['admin', 'store_manager', 'analyst'].includes(user?.role);
  const isStaff = ['stock_manager', 'billing_executive'].includes(user?.role);
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [showRecon, setShowRecon] = useState(false);
  const [reconPreview, setReconPreview] = useState(null);
  const [reconApplying, setReconApplying] = useState(false);
  const [reconDate, setReconDate] = useState(new Date().toISOString().split('T')[0]);

  const handleExportBoxes = async () => {
    try {
      const res = await API.get('/boxes/export/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `box_list.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Box list exported');
    } catch (err) { toast.error('Export failed'); }
  };

  const handleExportReconTemplate = async () => {
    try {
      const res = await API.get(`/boxes/reconciliation/export?date=${reconDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reconciliation_${reconDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reconciliation template exported');
    } catch (err) { toast.error('Export template failed'); }
  };

  const handleReconFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/boxes/reconciliation/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReconPreview(res.data);
      if (res.data.box_count === 0) toast.info('No changes detected in uploaded file');
      else toast.success(`${res.data.box_count} boxes with changes detected`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to parse file'); }
    e.target.value = '';
  };

  const handleApplyRecon = async () => {
    if (!reconPreview?.deltas?.length) return;
    if (!window.confirm(`Apply reconciliation to ${reconPreview.box_count} boxes? This will update their weights.`)) return;
    return run(async () => {
      setReconApplying(true);
      try {
        await API.post('/boxes/reconciliation/apply', { deltas: reconPreview.deltas, date: reconDate, notes: 'Box-wise reconciliation' });
        toast.success('Reconciliation applied successfully');
        setReconPreview(null);
        setShowRecon(false);
        fetchBoxes();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
      finally { setReconApplying(false); }
    });
  };

  const handleExportDelta = () => {
    if (!reconPreview?.deltas?.length) return;
    const headers = ['Box ID', 'Purity', 'Old Gross', 'New Gross', 'Delta Gross', 'Old Box Wt', 'New Box Wt', 'Delta Box Wt', 'Old Poly', 'New Poly', 'Delta Poly', 'Old Gold', 'New Gold', 'Delta Gold'];
    const rows = reconPreview.deltas.map(d => [d.box_id, d.purity, d.old_gross, d.new_gross, d.delta_gross, d.old_box_wt, d.new_box_wt, d.delta_box_wt, d.old_poly, d.new_poly, d.delta_poly, d.old_gold, d.new_gold, d.delta_gold]);
    rows.push(['TOTALS', '', '', '', reconPreview.totals.delta_gross, '', '', reconPreview.totals.delta_box_wt, '', '', reconPreview.totals.delta_poly, '', '', reconPreview.totals.delta_gold]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `reconciliation_delta_${reconDate}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchBoxes = async () => {
    try {
      const params = {};
      if (statusFilter === 'approved') {
        params.active_only = true;
      } else {
        params.active_only = false;
        params.status = statusFilter;
      }
      if (search) params.search = search;
      if (purityFilter !== 'all') params.purity = purityFilter;
      if (isStaff && (statusFilter === 'pending' || statusFilter === 'rejected')) {
        params.created_by_user_id = user?.id;
      }
      setBoxes((await API.get('/boxes', { params })).data);
    } catch(e) { console.error(e); }
  };
  const fetchItems = async () => { try { setAllItems((await API.get('/items')).data); } catch(e) {} };
  useEffect(() => { fetchBoxes(); }, [search, purityFilter, statusFilter]);
  useEffect(() => { fetchItems(); }, []);

  const autoGold = useMemo(() => {
    const g = parseFloat(form.gross_weight_g) || 0;
    const p = parseFloat(form.polyethene_weight_g) || 0;
    const b = parseFloat(form.box_weight_g) || 0;
    return (g - p - b).toFixed(3);
  }, [form.gross_weight_g, form.polyethene_weight_g, form.box_weight_g]);

  const toggleItem = (name, formKey, setFn) => {
    setFn(prev => {
      const list = prev[formKey] || [];
      return { ...prev, [formKey]: list.includes(name) ? list.filter(n => n !== name) : [...list, name] };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.description || !form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.gross_weight_g || parseFloat(form.gross_weight_g) <= 0) { toast.error('Gross weight is required'); return; }
    if (!form.box_weight_g || parseFloat(form.box_weight_g) <= 0) { toast.error('Box weight is required'); return; }
    if (!form.selectedItems || form.selectedItems.length === 0) { toast.error('At least one item name is required'); return; }
    return run(async () => {
    try {
      const res = await API.post('/boxes', {
        box_id: form.box_id, box_type: form.box_type, gold_purity: form.gold_purity,
        description: form.description,
        gross_weight_g: parseFloat(form.gross_weight_g) || 0,
        polyethene_weight_g: parseFloat(form.polyethene_weight_g) || 0,
        box_weight_g: parseFloat(form.box_weight_g) || 0,
        item_names: form.selectedItems,
        submitted_by: user?.role || 'stock_manager',
      });
      if (res.data?.status === 'warning' && res.data?.warning === 'negative_gold') {
        const proceed = window.confirm(
          `Warning: Gold weight would be negative (${res.data.computed_gold_g}g).\n\n` +
          `To maintain data coherence, gold will be set to 0g.\n\n` +
          `Gross: ${res.data.gross_g}g | Poly: ${res.data.poly_g}g | Box Wt: ${res.data.box_weight_g}g\n` +
          `Computed Gold: ${res.data.computed_gold_g}g (will be clamped to 0g)\n\n` +
          `Proceed anyway?`
        );
        if (!proceed) { return; }
        await API.post('/boxes', {
          box_id: form.box_id, box_type: form.box_type, gold_purity: form.gold_purity,
          description: form.description,
          gross_weight_g: parseFloat(form.gross_weight_g) || 0,
          polyethene_weight_g: parseFloat(form.polyethene_weight_g) || 0,
          box_weight_g: parseFloat(form.box_weight_g) || 0,
          item_names: form.selectedItems,
          submitted_by: user?.role || 'stock_manager',
          force: true,
        });
      }
      toast.success(canEdit ? 'Box created and active' : 'Box created, pending approval');
      setShowCreate(false);
      setForm({ box_id: '', box_type: 'regular', gold_purity: '76', description: '', gross_weight_g: '', polyethene_weight_g: '', box_weight_g: '', selectedItems: [] });
      fetchBoxes();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const openEdit = (box) => {
    setEditBox(box);
    setEditForm({
      box_type: box.box_type, gold_purity: box.gold_purity,
      description: box.description || '',
      gross_weight_g: String(box.gross_weight_g || 0),
      polyethene_weight_g: String(box.polyethene_weight_g || 0),
      box_weight_g: String(box.box_weight_g || 0),
      item_names: box.item_names || [],
    });
  };

  const editAutoGold = useMemo(() => {
    const g = parseFloat(editForm.gross_weight_g) || 0;
    const p = parseFloat(editForm.polyethene_weight_g) || 0;
    const b = parseFloat(editForm.box_weight_g) || 0;
    return (g - p - b).toFixed(3);
  }, [editForm.gross_weight_g, editForm.polyethene_weight_g, editForm.box_weight_g]);

  const handleEditSave = async () => {
    if (!editBox) return;
    return run(async () => {
    try {
      const res = await API.put(`/boxes/${editBox.box_id}`, {
        box_type: editForm.box_type || undefined,
        description: editForm.description,
        gross_weight_g: parseFloat(editForm.gross_weight_g) || 0,
        polyethene_weight_g: parseFloat(editForm.polyethene_weight_g) || 0,
        box_weight_g: parseFloat(editForm.box_weight_g) || 0,
        item_names: editForm.item_names,
        updated_by: user?.role || '',
      });
      if (res.data?.status === 'warning' && res.data?.warning === 'negative_gold') {
        const proceed = window.confirm(
          `Warning: Gold weight would be negative (${res.data.computed_gold_g}g).\n\n` +
          `To maintain data coherence, gold will be set to 0g.\n\n` +
          `Gross: ${res.data.gross_g}g | Poly: ${res.data.poly_g}g | Box Wt: ${res.data.box_weight_g}g\n` +
          `Computed Gold: ${res.data.computed_gold_g}g (will be clamped to 0g)\n\n` +
          `Proceed anyway?`
        );
        if (!proceed) return;
        await API.put(`/boxes/${editBox.box_id}`, {
          box_type: editForm.box_type || undefined,
          description: editForm.description,
          gross_weight_g: parseFloat(editForm.gross_weight_g) || 0,
          polyethene_weight_g: parseFloat(editForm.polyethene_weight_g) || 0,
          box_weight_g: parseFloat(editForm.box_weight_g) || 0,
          item_names: editForm.item_names,
          updated_by: user?.role || '',
          force: true,
        });
      }
      toast.success('Box updated');
      setEditBox(null); fetchBoxes();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleDelete = async (boxId) => {
    if (!window.confirm(`Delete box "${boxId}"?`)) return;
    return run(async () => {
      try { await API.delete(`/boxes/${boxId}`); toast.success('Box deleted'); fetchBoxes(); }
      catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleResubmit = async (boxId) => {
    return run(async () => {
      try {
        await API.post(`/boxes/${boxId}/resubmit`);
        toast.success('Box resubmitted for approval');
        fetchBoxes();
      } catch(err) { toast.error(err.response?.data?.detail || 'Resubmit failed'); }
    });
  };

  const handleApprove = async (boxId) => {
    return run(async () => {
      try {
        await API.post(`/boxes/${boxId}/approve`, { approved_by: user?.name || user?.role });
        toast.success(`Box ${boxId} approved`);
        fetchBoxes();
      } catch(err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
    });
  };

  const handleApproveAll = async () => {
    if (!window.confirm(`Approve all ${boxes.length} pending boxes?`)) return;
    return run(async () => {
      try {
        const res = await API.post('/boxes/approve-all', { approved_by: user?.name || user?.role });
        toast.success(res.data.message || 'All pending boxes approved');
        fetchBoxes();
      } catch(err) { toast.error(err.response?.data?.detail || 'Bulk approve failed'); }
    });
  };

  const handleQuickAddItem = async () => {
    const name = quickAddName.trim();
    if (!name) return;
    if (allItems.some(i => i.name.toLowerCase() === name.toLowerCase())) { toast.error('Item already exists'); return; }
    return run(async () => {
      try {
        await API.post('/items', { name, gold_purity: '', min_quantity: 0, supplier_name: '', box_ids: [] });
        toast.success(`Item "${name}" added`); setQuickAddName(''); fetchItems();
      } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const statusBadge = (box) => {
    const s = box.status || (box.is_active ? 'approved' : 'pending');
    if (s === 'approved') return <span className="status-badge status-approved text-xs">Active</span>;
    if (s === 'rejected') return <span className="status-badge status-rejected text-xs">Rejected</span>;
    if (s === 'deleted') return <span className="status-badge status-rejected text-xs">Deleted</span>;
    return <span className="status-badge status-pending text-xs">Pending</span>;
  };

  const statusTabs = canEdit
    ? [{ value: 'approved', label: 'Active' }, { value: 'pending', label: 'Pending' }, { value: 'rejected', label: 'Rejected' }]
    : [{ value: 'approved', label: 'Active' }, { value: 'pending', label: 'My Pending' }, { value: 'rejected', label: 'My Rejected' }];

  const sortedBoxes = useMemo(() => {
    const sorted = [...boxes];
    sorted.sort((a, b) => {
      if (sortCol === 'box_id') {
        const cmp = naturalBoxSort(a.box_id, b.box_id);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      va = String(va || '').toLowerCase(); vb = String(vb || '').toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return sorted;
  }, [boxes, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortHeader = ({ col, label, className }) => (
    <TableHead className={`cursor-pointer select-none ${className || ''}`} onClick={() => toggleSort(col)}>
      <div className="flex items-center gap-1">
        {label}
        {sortCol === col ? (sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={12} className="opacity-30" />}
      </div>
    </TableHead>
  );

  const totals = useMemo(() => {
    const t = { gross: 0, gold: 0, poly: 0 };
    const byPurity = { '76': { gross: 0, gold: 0, poly: 0 }, '84': { gross: 0, gold: 0, poly: 0 }, '92': { gross: 0, gold: 0, poly: 0 } };
    boxes.forEach(b => {
      t.gross += b.gross_weight_g || 0; t.gold += b.gold_weight_g || 0; t.poly += b.polyethene_weight_g || 0;
      const p = String(b.gold_purity);
      if (byPurity[p]) { byPurity[p].gross += b.gross_weight_g || 0; byPurity[p].gold += b.gold_weight_g || 0; byPurity[p].poly += b.polyethene_weight_g || 0; }
    });
    return { ...t, byPurity };
  }, [boxes]);

  const filteredCreateItems = useMemo(() => {
    let items = allItems;
    if (itemSearch) items = items.filter(i => i.name.toLowerCase().includes(itemSearch.toLowerCase()));
    const sel = form.selectedItems || [];
    return [...items].sort((a, b) => {
      const aS = sel.includes(a.name) ? 0 : 1;
      const bS = sel.includes(b.name) ? 0 : 1;
      return aS - bS;
    });
  }, [allItems, itemSearch, form.selectedItems]);

  const filteredEditItems = useMemo(() => {
    let items = allItems;
    if (editItemSearch) items = items.filter(i => i.name.toLowerCase().includes(editItemSearch.toLowerCase()));
    const sel = editForm.item_names || [];
    return [...items].sort((a, b) => {
      const aS = sel.includes(a.name) ? 0 : 1;
      const bS = sel.includes(b.name) ? 0 : 1;
      return aS - bS;
    });
  }, [allItems, editItemSearch, editForm.item_names]);

  return (
    <div data-testid="boxes-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Stock / Boxes</h1><p className="hidden sm:block">Manage all gold stock boxes</p></div>
        <div className="flex gap-2 w-full sm:w-auto">
          {canEdit && statusFilter === 'pending' && boxes.length > 0 && (
            <Button variant="outline" onClick={handleApproveAll} disabled={loading} data-testid="boxes-approve-all-button" className="flex-1 sm:flex-none text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10">
              <CheckCircle size={16} className="mr-2" />Approve All ({boxes.length})
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={handleExportBoxes} data-testid="boxes-export-button" className="flex-1 sm:flex-none">
              <Download size={16} className="mr-2" />Export
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowRecon(true)} data-testid="boxes-reconcile-button" className="flex-1 sm:flex-none text-amber-400 border-amber-500/30 hover:bg-amber-500/10">
              <FileSpreadsheet size={16} className="mr-2" />Reconcile Upload
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} data-testid="boxes-create-button" className="flex-1 sm:flex-none"><Plus size={16} className="mr-2" />New Box</Button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto">
        {statusTabs.map(t => (
          <button key={t.value} data-testid={`boxes-status-tab-${t.value}`}
            className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg border whitespace-nowrap transition-colors ${statusFilter === t.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}
            onClick={() => setStatusFilter(t.value)}>{t.label}</button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 mb-4">
        <div className="relative flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input data-testid="table-search-input" className="pl-9" placeholder="Search boxes..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={purityFilter} onValueChange={setPurityFilter}><SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="Purity" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="76">76</SelectItem><SelectItem value="84">84</SelectItem><SelectItem value="92">92</SelectItem></SelectContent></Select>
      </div>

      {/* Purity-wise Totals (admin & store_manager only) */}
      {boxes.length > 0 && ['admin', 'store_manager'].includes(user?.role) && (
        <div className="mb-4 space-y-2" data-testid="purity-totals-section">
          <div className="grid grid-cols-3 gap-2">
            {['76', '84', '92'].map(p => {
              const pt = totals.byPurity[p];
              const hasData = pt.gross > 0 || pt.gold > 0 || pt.poly > 0;
              if (!hasData) return null;
              return (
                <div key={p} className="p-2 rounded-lg border text-center" style={{ background: 'hsl(43, 50%, 10%)', borderColor: 'hsl(43, 50%, 25%)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'hsl(43, 80%, 65%)' }}>Purity {p}</p>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Gross: <span className="font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>{pt.gross.toFixed(3)}g</span></p>
                    <p className="text-xs text-muted-foreground">Gold: <span className="font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>{pt.gold.toFixed(3)}g</span></p>
                    <p className="text-xs text-muted-foreground">Poly: <span className="font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>{pt.poly.toFixed(3)}g</span></p>
                  </div>
                </div>
              );
            })}
          </div>
          {user?.role === 'admin' && (
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg border text-center" style={{ background: 'hsl(210, 30%, 12%)', borderColor: 'hsl(210, 30%, 25%)' }}>
                <p className="text-xs text-muted-foreground">Grand Gross</p>
                <p className="font-mono font-bold text-sm" style={{ color: 'hsl(43, 96%, 56%)' }}>{totals.gross.toFixed(3)}g</p>
              </div>
              <div className="p-2 rounded-lg border text-center" style={{ background: 'hsl(210, 30%, 12%)', borderColor: 'hsl(210, 30%, 25%)' }}>
                <p className="text-xs text-muted-foreground">Grand Gold</p>
                <p className="font-mono font-bold text-sm" style={{ color: 'hsl(43, 96%, 56%)' }}>{totals.gold.toFixed(3)}g</p>
              </div>
              <div className="p-2 rounded-lg border text-center" style={{ background: 'hsl(210, 30%, 12%)', borderColor: 'hsl(210, 30%, 25%)' }}>
                <p className="text-xs text-muted-foreground">Grand Poly</p>
                <p className="font-mono font-bold text-sm" style={{ color: 'hsl(43, 96%, 56%)' }}>{totals.poly.toFixed(3)}g</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile */}
      <div className="mobile-card-list">
        {boxes.map(box => (
          <Card key={box.id} data-testid={`box-mobile-card-${box.box_id}`} className={box.status === 'rejected' ? 'border-destructive/50' : ''}>
            <CardContent className="p-3">
              {/* Rejection Message */}
              {box.status === 'rejected' && box.rejection_message && (
                <div className="mb-2 p-2 rounded-lg text-xs flex items-start gap-2" style={{ background: 'hsl(0, 50%, 14%)', color: 'hsl(0, 80%, 70%)', border: '1px solid hsl(0, 50%, 25%)' }}>
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <div><b>Rejected:</b> {box.rejection_message}</div>
                </div>
              )}
              <div className="flex items-center justify-between mb-1" onClick={() => navigate(`/boxes/${box.box_id}`)}>
                <span className="font-mono font-semibold text-sm">{box.box_id}</span>
                <div className="flex gap-1">
                  <span className={`status-badge purity-${box.gold_purity} text-xs`}>{box.gold_purity}</span>
                  {statusBadge(box)}
                </div>
              </div>
              {box.description && <p className="text-xs text-muted-foreground mb-1 truncate">{box.description}</p>}
              <div className="grid grid-cols-3 gap-1 text-xs">
                <div><span className="text-muted-foreground">Gross:</span> <span className="weight-value">{formatWeight(box.gross_weight_g)}</span></div>
                <div><span className="text-muted-foreground">Gold:</span> <span className="weight-value">{formatWeight(box.gold_weight_g)}</span></div>
                <div><span className="text-muted-foreground">Poly:</span> <span className="weight-value">{formatWeight(box.polyethene_weight_g)}</span></div>
              </div>
              {(box.item_names || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{box.item_names.slice(0, 4).map((n, i) => <span key={i} className="text-xs bg-muted px-1.5 py-0.5 rounded">{n}</span>)}</div>}
              <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-border/50">
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => navigate(`/boxes/${box.box_id}`)}><Eye size={14} className="mr-1" />View</Button>
                {canEdit && (
                  <>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(box)} data-testid={`box-mobile-edit-${box.box_id}`}><Pencil size={14} className="mr-1" />Edit</Button>
                    {box.status === 'approved' && <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-destructive" onClick={() => handleDelete(box.box_id)}><Trash2 size={14} className="mr-1" />Delete</Button>}
                    {(box.status === 'pending' || box.status === 'rejected') && (
                      <Button variant="outline" size="sm" className="h-8 px-2 text-xs text-emerald-500 border-emerald-500/30" onClick={() => handleApprove(box.box_id)} data-testid={`box-approve-${box.box_id}`}><CheckCircle size={14} className="mr-1" />Approve</Button>
                    )}
                  </>
                )}
                {isStaff && box.status === 'rejected' && (
                  <><Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(box)}><Pencil size={14} className="mr-1" />Edit</Button>
                  <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => handleResubmit(box.box_id)} data-testid={`box-resubmit-${box.box_id}`}><RotateCcw size={14} className="mr-1" />Resubmit</Button></>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {boxes.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No boxes found</p>}
      </div>

      {/* Desktop */}
      <div className="desktop-table"><Card><CardContent className="p-0"><div className="table-scroll-wrapper"><Table className="data-table"><TableHeader><TableRow>
        <SortHeader col="box_id" label="Box ID" /><SortHeader col="box_type" label="Type" /><SortHeader col="gold_purity" label="Purity" />
        <SortHeader col="gross_weight_g" label="Gross" className="text-right" />
        <SortHeader col="gold_weight_g" label="Gold" className="text-right" />
        <SortHeader col="polyethene_weight_g" label="Poly" className="text-right" /><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
      </TableRow></TableHeader><TableBody>
        {sortedBoxes.map(box => (
          <TableRow key={box.id} className={box.status === 'rejected' ? 'bg-destructive/5' : ''}>
            <TableCell>
              <div>
                <span className="font-mono font-semibold">{box.box_id}</span>
                {box.description && <p className="text-xs text-muted-foreground truncate max-w-[150px]">{box.description}</p>}
                {box.status === 'rejected' && box.rejection_message && (
                  <p className="text-xs text-destructive mt-0.5 truncate max-w-[200px]">Rejected: {box.rejection_message}</p>
                )}
              </div>
            </TableCell>
            <TableCell><Badge variant={box.box_type === 'brand' ? 'default' : 'secondary'}>{box.box_type}</Badge></TableCell>
            <TableCell><span className={`status-badge purity-${box.gold_purity}`}>{box.gold_purity}</span></TableCell>
            <TableCell className="text-right weight-value">{formatWeight(box.gross_weight_g)}</TableCell>
            <TableCell className="text-right weight-value">{formatWeight(box.gold_weight_g)}</TableCell>
            <TableCell className="text-right weight-value">{formatWeight(box.polyethene_weight_g)}</TableCell>
            <TableCell><div className="flex flex-wrap gap-1">{(box.item_names || []).slice(0, 3).map((n, i) => <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded">{n}</span>)}</div></TableCell>
            <TableCell>{statusBadge(box)}</TableCell>
            <TableCell><div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/boxes/${box.box_id}`)} data-testid={`box-view-${box.box_id}`}><Eye size={16} /></Button>
              {canEdit && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(box)} data-testid={`box-edit-${box.box_id}`}><Pencil size={14} /></Button>
                  {box.status === 'approved' && <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(box.box_id)} data-testid={`box-delete-${box.box_id}`}><Trash2 size={14} /></Button>}
                  {(box.status === 'pending' || box.status === 'rejected') && (
                    <Button variant="ghost" size="icon" className="text-emerald-500" onClick={() => handleApprove(box.box_id)} data-testid={`box-approve-${box.box_id}`}><CheckCircle size={14} /></Button>
                  )}
                </>
              )}
              {isStaff && box.status === 'rejected' && (
                <><Button variant="ghost" size="icon" onClick={() => openEdit(box)}><Pencil size={14} /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleResubmit(box.box_id)} data-testid={`box-resubmit-${box.box_id}`}><RotateCcw size={14} /></Button></>
              )}
            </div></TableCell>
          </TableRow>
        ))}
        {boxes.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No boxes found</TableCell></TableRow>}
      </TableBody></Table></div></CardContent></Card></div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Create New Box</DialogTitle></DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium mb-1 block">Box ID *</label><Input data-testid="box-create-id" value={form.box_id} onChange={e => setForm({...form, box_id: e.target.value})} placeholder="e.g. BOX-A1" required /></div>
            <div><label className="text-sm font-medium mb-1 block">Type</label><Select value={form.box_type} onValueChange={v => setForm({...form, box_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="regular">Regular</SelectItem><SelectItem value="brand">Brand Item</SelectItem></SelectContent></Select></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Gold Purity *</label><Select value={form.gold_purity} onValueChange={v => setForm({...form, gold_purity: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="76">76</SelectItem><SelectItem value="84">84</SelectItem><SelectItem value="92">92</SelectItem></SelectContent></Select></div>
          <div><label className="text-sm font-medium mb-1 block">Description *</label><Textarea data-testid="box-create-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Box description (required)..." rows={2} className="text-sm" required /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><label className="text-sm font-medium mb-1 block">Gross Weight (g) *</label><DecimalInput data-testid="box-create-gross" value={form.gross_weight_g} onChange={v => setForm(f => ({...f, gross_weight_g: v}))} placeholder="0.000" /></div>
            <div><label className="text-sm font-medium mb-1 block">Poly Weight (g)</label><DecimalInput value={form.polyethene_weight_g} onChange={v => setForm(f => ({...f, polyethene_weight_g: v}))} placeholder="0.000" /></div>
            <div><label className="text-sm font-medium mb-1 block">Box Weight (g) *</label><DecimalInput data-testid="box-create-boxwt" value={form.box_weight_g} onChange={v => setForm(f => ({...f, box_weight_g: v}))} placeholder="0.000" /></div>
          </div>
          <div className="p-3 rounded-lg border" style={{ background: parseFloat(autoGold) < 0 ? 'hsl(0, 50%, 14%)' : 'hsl(43, 50%, 14%)', borderColor: parseFloat(autoGold) < 0 ? 'hsl(0, 50%, 25%)' : 'hsl(43, 50%, 25%)' }}>
            <div className="flex items-center justify-between"><span className="text-sm font-medium" style={{ color: parseFloat(autoGold) < 0 ? 'hsl(0, 80%, 65%)' : 'hsl(43, 80%, 65%)' }}>Net Gold Weight (auto)</span><span className="text-lg font-mono font-bold" style={{ color: parseFloat(autoGold) < 0 ? 'hsl(0, 96%, 56%)' : 'hsl(43, 96%, 56%)' }}>{autoGold}g</span></div>
            <p className="text-xs text-muted-foreground mt-1">= Gross - Poly - Box Weight</p>
            {parseFloat(autoGold) < 0 && <p className="text-xs mt-1" style={{ color: 'hsl(0, 80%, 65%)' }}>Gold is negative — will be clamped to 0g if you proceed</p>}
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Item Names *</label>
            <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Search items..." className="text-sm h-8 mb-2" data-testid="item-search-create" />
            {filteredCreateItems.length > 0 ? (
              <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg border bg-muted/30">
                {filteredCreateItems.map(item => (
                  <label key={item.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-muted/50 text-sm">
                    <Checkbox checked={form.selectedItems.includes(item.name)} onCheckedChange={() => toggleItem(item.name, 'selectedItems', setForm)} /><span>{item.name}</span>
                  </label>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground p-2 border rounded-lg bg-muted/30">{allItems.length === 0 ? 'No items yet. Add below or from Item Names page.' : 'No items match search.'}</p>}
            {form.selectedItems.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{form.selectedItems.map((n, i) => <Badge key={i} variant="outline" className="text-xs">{n}</Badge>)}</div>}
            {canManageItems && <div className="flex gap-2 mt-2"><Input value={quickAddName} onChange={e => setQuickAddName(e.target.value)} placeholder="Quick add item..." className="text-sm h-8" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleQuickAddItem(); } }} /><Button type="button" size="sm" variant="outline" className="h-8 shrink-0" onClick={handleQuickAddItem}><Plus size={14} className="mr-1" />Add</Button></div>}
          </div>
          {!canEdit && <p className="text-xs text-muted-foreground text-center p-2 rounded-lg bg-muted/30">This box will need admin/store manager approval before becoming active.</p>}
          <Button type="submit" className="w-full" disabled={loading} data-testid="box-create-submit">{loading ? 'Creating...' : 'Create Box'}</Button>
        </form>
      </DialogContent></Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editBox} onOpenChange={() => setEditBox(null)}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Box: {editBox?.box_id}</DialogTitle></DialogHeader>
        {editBox && (<div className="space-y-4">
          {editBox.status === 'rejected' && editBox.rejection_message && (
            <div className="p-3 rounded-lg text-sm flex items-start gap-2" style={{ background: 'hsl(0, 50%, 14%)', color: 'hsl(0, 80%, 70%)', border: '1px solid hsl(0, 50%, 25%)' }}>
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div><b>Rejection reason:</b> {editBox.rejection_message}</div>
            </div>
          )}
          {canEdit && editBox.status !== 'approved' && (
            <div className="p-2 rounded-lg text-xs text-center" style={{ background: 'hsl(120, 50%, 14%)', color: 'hsl(120, 80%, 65%)', border: '1px solid hsl(120, 50%, 25%)' }}>
              Saving will auto-approve this box
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium mb-1 block">Type</label><Select value={editForm.box_type} onValueChange={v => setEditForm({...editForm, box_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="regular">Regular</SelectItem><SelectItem value="brand">Brand Item</SelectItem></SelectContent></Select></div>
            <div><label className="text-sm font-medium mb-1 block flex items-center gap-1.5">Purity <Lock size={12} className="text-muted-foreground" /></label><div className="h-10 flex items-center px-3 rounded-md border bg-muted/60 cursor-not-allowed"><span className={`status-badge purity-${editForm.gold_purity} text-xs`}>{editForm.gold_purity}</span><span className="ml-2 text-xs text-muted-foreground">Fixed</span></div></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Description *</label><Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} className="text-sm" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Gross (g)</label><DecimalInput value={editForm.gross_weight_g} onChange={v => setEditForm(f => ({...f, gross_weight_g: v}))} /></div>
            <div><label className="text-sm font-medium mb-1 block">Poly (g)</label><DecimalInput value={editForm.polyethene_weight_g} onChange={v => setEditForm(f => ({...f, polyethene_weight_g: v}))} /></div>
            <div><label className="text-sm font-medium mb-1 block">Box Wt (g)</label><DecimalInput value={editForm.box_weight_g} onChange={v => setEditForm(f => ({...f, box_weight_g: v}))} /></div>
          </div>
          <div className="p-2 rounded-lg border text-center" style={{ background: parseFloat(editAutoGold) < 0 ? 'hsl(0, 50%, 14%)' : 'hsl(43, 50%, 14%)', borderColor: parseFloat(editAutoGold) < 0 ? 'hsl(0, 50%, 25%)' : 'hsl(43, 50%, 25%)' }}>
            <span className="text-xs" style={{ color: parseFloat(editAutoGold) < 0 ? 'hsl(0, 80%, 65%)' : 'hsl(43, 80%, 65%)' }}>Net Gold: </span><span className="font-mono font-bold" style={{ color: parseFloat(editAutoGold) < 0 ? 'hsl(0, 96%, 56%)' : 'hsl(43, 96%, 56%)' }}>{editAutoGold}g</span>
            {parseFloat(editAutoGold) < 0 && <span className="text-xs ml-2" style={{ color: 'hsl(0, 80%, 65%)' }}>(will be clamped to 0g)</span>}
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Item Names *</label>
            <Input value={editItemSearch} onChange={e => setEditItemSearch(e.target.value)} placeholder="Search items..." className="text-sm h-8 mb-2" />
            {filteredEditItems.length > 0 && <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg border bg-muted/30">
              {filteredEditItems.map(item => (
                <label key={item.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-muted/50 text-sm">
                  <Checkbox checked={(editForm.item_names || []).includes(item.name)} onCheckedChange={() => toggleItem(item.name, 'item_names', setEditForm)} /><span>{item.name}</span>
                </label>
              ))}
            </div>}
            {canManageItems && <div className="flex gap-2 mt-2"><Input value={quickAddName} onChange={e => setQuickAddName(e.target.value)} placeholder="Quick add item..." className="text-sm h-8" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleQuickAddItem(); } }} /><Button type="button" size="sm" variant="outline" className="h-8 shrink-0" onClick={handleQuickAddItem}><Plus size={14} className="mr-1" />Add</Button></div>}
          </div>
          <Button onClick={handleEditSave} disabled={loading} className="w-full" data-testid="box-edit-save">{loading ? 'Saving…' : 'Save Changes'}</Button>
        </div>)}
      </DialogContent></Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={showRecon} onOpenChange={(open) => { setShowRecon(open); if (!open) setReconPreview(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} /> Box-wise Reconciliation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Step 1: Export template */}
            <div className="p-3 rounded-lg border bg-muted/30">
              <p className="text-sm font-semibold mb-2">Step 1: Export Current Weights</p>
              <div className="flex items-center gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Date</label>
                  <Input type="date" value={reconDate} onChange={e => setReconDate(e.target.value)} className="h-8 text-xs w-40" data-testid="recon-date-input" />
                </div>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleExportReconTemplate} data-testid="recon-export-btn">
                  <Download size={14} className="mr-1" />Export Template
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Download the Excel file, modify weights for boxes that need reconciliation, then upload below.</p>
            </div>

            {/* Step 2: Upload modified file */}
            <div className="p-3 rounded-lg border bg-muted/30">
              <p className="text-sm font-semibold mb-2">Step 2: Upload Modified File</p>
              <label className="cursor-pointer">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/50 transition-colors">
                  <Upload size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to upload modified Excel file</span>
                </div>
                <input type="file" accept=".xlsx,.xls" onChange={handleReconFileUpload} className="hidden" data-testid="recon-file-input" />
              </label>
            </div>

            {/* Step 3: Delta Preview */}
            {reconPreview && reconPreview.box_count > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">{reconPreview.box_count} Boxes Changed</p>
                  <Button variant="ghost" size="sm" onClick={handleExportDelta} data-testid="recon-export-delta-btn">
                    <Download size={14} className="mr-1" />Export Delta
                  </Button>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Box ID</TableHead>
                        <TableHead className="text-xs text-right">Old Gross</TableHead>
                        <TableHead className="text-xs text-right">New Gross</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                        <TableHead className="text-xs text-right">Old Box Wt</TableHead>
                        <TableHead className="text-xs text-right">New Box Wt</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                        <TableHead className="text-xs text-right">Old Poly</TableHead>
                        <TableHead className="text-xs text-right">New Poly</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                        <TableHead className="text-xs text-right">Old Gold</TableHead>
                        <TableHead className="text-xs text-right">New Gold</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reconPreview.deltas.map(d => (
                        <TableRow key={d.box_id}>
                          <TableCell className="font-mono text-xs font-semibold">{d.box_id} <span className={`status-badge purity-${d.purity} text-[9px] ml-1`}>{d.purity}</span></TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.old_gross.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.new_gross.toFixed(3)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-bold ${d.delta_gross > 0 ? 'text-emerald-400' : d.delta_gross < 0 ? 'text-rose-400' : ''}`}>{d.delta_gross > 0 ? '+' : ''}{d.delta_gross.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.old_box_wt.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.new_box_wt.toFixed(3)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-bold ${d.delta_box_wt > 0 ? 'text-emerald-400' : d.delta_box_wt < 0 ? 'text-rose-400' : ''}`}>{d.delta_box_wt > 0 ? '+' : ''}{d.delta_box_wt.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.old_poly.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.new_poly.toFixed(3)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-bold ${d.delta_poly > 0 ? 'text-emerald-400' : d.delta_poly < 0 ? 'text-rose-400' : ''}`}>{d.delta_poly > 0 ? '+' : ''}{d.delta_poly.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.old_gold.toFixed(3)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{d.new_gold.toFixed(3)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-bold ${d.delta_gold > 0 ? 'text-emerald-400' : d.delta_gold < 0 ? 'text-rose-400' : ''}`}>{d.delta_gold > 0 ? '+' : ''}{d.delta_gold.toFixed(3)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="border-t-2 border-primary/30 font-bold">
                        <TableCell className="text-xs font-bold">TOTALS</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${reconPreview.totals.delta_gross > 0 ? 'text-emerald-400' : reconPreview.totals.delta_gross < 0 ? 'text-rose-400' : ''}`}>{reconPreview.totals.delta_gross > 0 ? '+' : ''}{reconPreview.totals.delta_gross.toFixed(3)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${reconPreview.totals.delta_box_wt > 0 ? 'text-emerald-400' : reconPreview.totals.delta_box_wt < 0 ? 'text-rose-400' : ''}`}>{reconPreview.totals.delta_box_wt > 0 ? '+' : ''}{reconPreview.totals.delta_box_wt.toFixed(3)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${reconPreview.totals.delta_poly > 0 ? 'text-emerald-400' : reconPreview.totals.delta_poly < 0 ? 'text-rose-400' : ''}`}>{reconPreview.totals.delta_poly > 0 ? '+' : ''}{reconPreview.totals.delta_poly.toFixed(3)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${reconPreview.totals.delta_gold > 0 ? 'text-emerald-400' : reconPreview.totals.delta_gold < 0 ? 'text-rose-400' : ''}`}>{reconPreview.totals.delta_gold > 0 ? '+' : ''}{reconPreview.totals.delta_gold.toFixed(3)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <Button onClick={handleApplyRecon} disabled={reconApplying} className="w-full" data-testid="recon-apply-btn">
                  {reconApplying ? 'Applying...' : `Accept Modifications (${reconPreview.box_count} boxes)`}
                </Button>
              </div>
            )}
            {reconPreview && reconPreview.box_count === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No weight changes detected in the uploaded file.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
